// The Featured Listings MLS ID table, row by row, as the page draws it: each board's billable agents by name, each
// followed by their extra rows, then the rows no agent claims, with the rows that need review first. The states come
// from ../featured-table.ts; this adds the words, the problem tags and the one action each row offers.
import { FeaturedRow, FeaturedSlot, featuredTable } from "../featured-table";
import { AgentMlsIds } from "model/agent-mls-ids";

// What a row's action opens.
export type RowAction =
	// Edit on an agent's slot: Edit for an agent turned off, Set for Not set. The slot's own slotAction decides.
	| { kind: "editSlot"; agentId: number; boardId: number }
	// Edit on a linked row.
	| { kind: "editLinked"; agentId: number; boardId: number; entryId: number }
	// Review on a row with a problem. keepOne names the value's one holder.
	| { kind: "keepOne"; boardId: number; entryId: number }
	| { kind: "chooseOwner"; clientBoardId: number }
	| { kind: "remove"; clientBoardId: number };

// The problem tag beside the MLS ID, with its tooltip.
export interface RowIssue {
	label: "Duplicate" | "Shared" | "Not Billable" | "Unknown";
	tip: string;
}

export interface DisplayRow {
	key: string;
	boardId: number;
	// The agents named in the Agent column, each linking to their profile. Empty for an Unknown row.
	agents: { id: number; name: string }[];
	// No agent claims the row.
	unknown: boolean;
	// null: Not set, shown as "None".
	mlsId: string | null;
	// null: none, shown as "None".
	alternateId: string | null;
	issue: RowIssue | null;
	// The check in the On Featured Listings column.
	onFeatured: boolean;
	// The info icon in the On Featured Listings column, for an agent turned off.
	featuredInfo: string | null;
	// Review for a row with a problem, otherwise Edit.
	label: "Edit" | "Review";
	action: RowAction;
}

const OFF_INFO = "Kept off the Featured Listings page. This MLS ID still powers the bio page and lead routing.";

const agentsNamed = (agentMlsIds: AgentMlsIds, agentIds: number[]) =>
	agentIds.map((id) => agentMlsIds.agents.get(id)).filter((a): a is NonNullable<typeof a> => !!a).map((a) => ({ id: a.id, name: a.name }));

const listNames = (names: string[]) =>
	names.length <= 2 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// The entry a row is paired with, or a duplicate's one holder.
const holderOf = (agentMlsIds: AgentMlsIds, clientBoardId: number) =>
	agentMlsIds.entries.find((e) => agentMlsIds.holderOf.get(clientBoardId) === e.id)
		?? agentMlsIds.entries.find((e) => e.needsSlot && e.clientBoardIds.includes(clientBoardId))!;

// Why a row whose value only non-billable or opted-out rows hold is an orphan.
const notBillableTip = (agentMlsIds: AgentMlsIds, row: FeaturedRow, names: string[]) =>
	row.agentIds.every((id) => !agentMlsIds.agents.get(id)?.billable)
		? `${row.mlsId} belongs to ${listNames(names)}, but they aren't billable. Make them billable, or delete it.`
		: `${row.mlsId} is kept off Featured Listings on ${listNames(names)}'s profile. Delete it from Featured Listings.`;

const featuredRowDisplay = (agentMlsIds: AgentMlsIds, row: FeaturedRow): DisplayRow => {
	const agents = agentsNamed(agentMlsIds, row.agentIds);
	const base = {
		key: `row-${row.clientBoardId}`,
		boardId: row.boardId,
		agents,
		unknown: row.state === "unknown",
		mlsId: row.mlsId,
		alternateId: row.alternateId,
		featuredInfo: null,
	};
	if (row.state === "aligned") {
		const entryId = holderOf(agentMlsIds, row.clientBoardId).id;
		return { ...base, issue: null, onFeatured: true, label: "Edit", action: { kind: "editLinked", agentId: row.agentIds[0], boardId: row.boardId, entryId } };
	}
	const names = agents.map((a) => a.name);
	const issue: RowIssue = row.state === "duplicate"
		? { label: "Duplicate", tip: row.fix === "keepOne"
			? `${row.mlsId} is on Featured Listings ${holderOf(agentMlsIds, row.clientBoardId).clientBoardIds.length} times for ${names[0]}. Choose the one to keep.`
			: `${row.mlsId} is on Featured Listings more times than it's on ${listNames(names)}'s profiles. Delete the extra.` }
		: row.state === "shared" ? { label: "Shared", tip: `${row.mlsId} is assigned to ${listNames(names)}. Choose who it belongs to, or delete it.` }
		: row.state === "nonBillable" ? { label: "Not Billable", tip: notBillableTip(agentMlsIds, row, names) }
		: { label: "Unknown", tip: "This Agent MLS ID isn't assigned to any agent. Choose the agent it belongs to, or delete it." };
	const action: RowAction = row.fix === "keepOne"
		? { kind: "keepOne", boardId: row.boardId, entryId: holderOf(agentMlsIds, row.clientBoardId).id }
		: row.fix === "chooseOwner" ? { kind: "chooseOwner", clientBoardId: row.clientBoardId }
		: { kind: "remove", clientBoardId: row.clientBoardId };
	return { ...base, issue, onFeatured: false, label: "Review", action };
};

// The two reads reload separately after a write, so for a moment an agent's entry can name a Featured row the
// client boards no longer have. A slot on a missing row is skipped (null) until both reads agree.
const slotDisplay = (agentMlsIds: AgentMlsIds, slot: FeaturedSlot, rows: Record<number, FeaturedRow>): DisplayRow | null => {
	const row = slot.clientBoardId != null ? rows[slot.clientBoardId] : undefined;
	const onRow = slot.state === "on" || slot.state === "shared";
	if (onRow && !row) {
		return null;
	}
	if (slot.state === "on" && row) {
		return featuredRowDisplay(agentMlsIds, row);
	}
	const agents = agentsNamed(agentMlsIds, [slot.agentId]);
	const base = { key: `slot-${slot.agentId}-${slot.boardId}`, boardId: slot.boardId, agents, unknown: false, onFeatured: false };
	if (slot.state === "shared" && row) {
		const others = agentsNamed(agentMlsIds, row.agentIds.filter((id) => id !== slot.agentId)).map((a) => a.name);
		return {
			...base,
			mlsId: row.mlsId,
			alternateId: row.alternateId,
			issue: { label: "Shared", tip: `${row.mlsId} is assigned to both ${agents[0]?.name ?? ""} and ${listNames(others)}. Choose who it belongs to, or delete it.` },
			featuredInfo: null,
			label: "Review",
			action: { kind: "chooseOwner", clientBoardId: row.clientBoardId },
		};
	}
	const off = slot.state === "off";
	const held = {
		...base,
		mlsId: off ? slot.mlsId : null,
		alternateId: off ? slot.alternateId : null,
		featuredInfo: off ? OFF_INFO : null,
	};
	// Not set, including an agent with only strays, which never show here (slotAction).
	return { ...held, issue: null, label: "Edit", action: { kind: "editSlot", agentId: slot.agentId, boardId: slot.boardId } };
};

// One board's rows, the rows that need review first, then the rest, each in the table's order.
export const featuredDisplayRows = (agentMlsIds: AgentMlsIds, boardId: number): DisplayRow[] => {
	const table = featuredTable(agentMlsIds, boardId);
	const out: DisplayRow[] = [];
	const rowDisplay = (id: number) => {
		if (table.rows[id]) {
			out.push(featuredRowDisplay(agentMlsIds, table.rows[id]));
		}
	};
	table.slots.forEach((slot) => {
		const display = slotDisplay(agentMlsIds, slot, table.rows);
		if (display) {
			out.push(display);
		}
		slot.extraClientBoardIds.forEach(rowDisplay);
	});
	table.unclaimed.forEach(rowDisplay);
	return problemsFirst(out);
};

// The rows that need review first, keeping each group's order.
export const problemsFirst = (rows: DisplayRow[]): DisplayRow[] =>
	[...rows.filter((r) => r.issue), ...rows.filter((r) => !r.issue)];
